import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Quote, Mail } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { TESTIMONIALS } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Community = () => {
  const [email, setEmail] = useState("");
  const [idx, setIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const t = setInterval(
      () => setIdx((i) => (i + 1) % TESTIMONIALS.length),
      5000
    );
    return () => clearInterval(t);
  }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/newsletter`, { email, source: "community" });
      toast.success("Welcome to the family.");
      setEmail("");
    } catch (err) {
      toast.error("Couldn't subscribe right now.");
    } finally {
      setSubmitting(false);
    }
  };

  const current = TESTIMONIALS[idx];

  return (
    <section
      id="contact"
      data-testid="community-section"
      className="relative py-24 sm:py-32 lg:py-40 bg-[#050505]"
    >
      <div className="max-w-[1100px] mx-auto px-6 sm:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          <div className="flex items-center justify-center gap-4 mb-4">
            <span className="h-px w-10 bg-[color:var(--gold)]" />
            <span className="overline">The Family</span>
            <span className="h-px w-10 bg-[color:var(--gold)]" />
          </div>
          <h2 className="font-condensed text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">
            Join the <span className="font-display italic gold-text">movement.</span>
          </h2>
          <p className="mt-5 text-white/55 max-w-xl mx-auto">
            Get first access to drops, tour dates, and unreleased music. No spam — just
            the next chapter.
          </p>

          <form
            onSubmit={subscribe}
            data-testid="newsletter-form"
            className="mt-8 flex flex-col sm:flex-row gap-3 max-w-xl mx-auto"
          >
            <div className="flex items-center gap-3 glass-strong rounded-full px-5 py-3 flex-1">
              <Mail size={16} className="text-white/50" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                data-testid="newsletter-email"
                required
                className="flex-1 bg-transparent outline-none text-sm"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="newsletter-submit"
              className="btn-gold whitespace-nowrap disabled:opacity-60"
            >
              {submitting ? "Joining…" : "Join VIP"}
            </button>
          </form>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="mt-14 glass rounded-2xl p-8 sm:p-10 relative overflow-hidden"
        >
          <Quote
            className="absolute top-6 right-6 gold-text opacity-25"
            size={56}
          />
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl"
          >
            <p className="font-display text-xl sm:text-2xl italic text-white/90 leading-snug">
              &ldquo;{current.quote}&rdquo;
            </p>
            <p className="mt-4 overline">
              {current.author} — {current.role}
            </p>
          </motion.div>
          <div className="flex gap-2 mt-6">
            {TESTIMONIALS.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                data-testid={`testimonial-dot-${i}`}
                className={`h-1 rounded-full transition-all ${
                  i === idx
                    ? "bg-[color:var(--gold)] w-8"
                    : "bg-white/20 w-4"
                }`}
                aria-label={`Testimonial ${i + 1}`}
              />
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
